import React from 'react';
import { get, startCase, inRange, isNumber } from 'lodash';
import { Table, TableBody, TableCell, TableHead, TableRow, TextField, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import { formatCurrencyFloat, formatInt } from 'shared/utils/numberFormatters';
import { EXPENSE_COMPS_DEFAULT_FILTERS, PRICE_PER_SQUARE_FEET_POSTFIX, PRICE_PER_UNIT_POSTFIX } from './constants';
import { Filter, FilterValues, SubjectExpenses, PropertySummary, TotalOperatingExpenses } from './types';
import { DateTime } from 'luxon';
import { BASIS_TYPES } from 'shared/constants/expenses';

interface Props {
  filters?: Filter[];
  propertySummary: PropertySummary;
  subjectExpenses: SubjectExpenses;
  totalSubjectOperatingExpenses?: TotalOperatingExpenses;
  expenseBasis: 'sf' | 'unit';
  includeRETaxes: boolean;
  utilitiesExpensesMode: string;
  filterValues: FilterValues;
  onExpenseBasisChange: (value: 'sf' | 'unit') => void;
  onIncludeRETaxesChange: (value: boolean) => void;
  onUtilitiesExpensesModeChange: (value: string) => void;
  onFilterValueChange: (filterName: string, type: 'min' | 'max', value: number | undefined) => void;
}

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  fontSize: 14,
  padding: theme.spacing(1),
}));

const StyledStaticCell = styled('span')(({ theme }) => ({
  fontSize: 12,
  color: theme.palette.text.secondary,
}));

const StyledNumberField = styled(TextField)(({ theme }) => ({
  '& .MuiInputBase-input': {
    fontSize: 14,
    padding: theme.spacing(0.5),
    textAlign: 'left',
  },
  '& .MuiOutlinedInput-root': {
    '& fieldset': {
      borderColor: theme.palette.divider,
    },
    '&:hover fieldset': {
      borderColor: theme.palette.primary.main,
    },
  },
}));

const validateYearBuilt = (value: number): string | null => {
  const maxYear = DateTime.now().year + 1;
  if (isNumber(value) && !inRange(value, 1800, maxYear)) {
    return 'Enter a valid year';
  }
  return null;
};

const validateExpenseYear = (value: number): string | null => {
  const maxYear = DateTime.now().year + 1;
  if (isNumber(value) && !inRange(value, 1970, maxYear)) {
    return 'Enter a valid year';
  }
  return null;
};

const dateValidators: Record<string, (value: number) => string | null> = {
  yearBuilt: validateYearBuilt,
  expenseYear: validateExpenseYear,
};

const ExpenseCompsFiltersTable: React.FC<Props> = ({
  filters = [],
  propertySummary,
  subjectExpenses,
  totalSubjectOperatingExpenses,
  expenseBasis,
  filterValues,
  onFilterValueChange,
}) => {
  const getStaticCell = (item: (typeof EXPENSE_COMPS_DEFAULT_FILTERS)[0]) => {
    let value = get(propertySummary, item.propertyName);

    if (item.isNumeric) {
      value = formatInt(value);
    }

    if (item.name === 'totalOperatingExpenses') {
      const psf = formatCurrencyFloat(get(totalSubjectOperatingExpenses, BASIS_TYPES.psf) || 0);
      value = `${psf}/SF`;
    }

    if (item.name === 'totalOperatingExpensesPerUnit') {
      const perUnit = formatCurrencyFloat(get(totalSubjectOperatingExpenses, BASIS_TYPES.perUnit) || 0);
      value = `${perUnit}/Unit`;
    }

    if ('isString' in item && item.isString) {
      value = startCase(value);
    }

    return <StyledStaticCell>{value || 'N/A'}</StyledStaticCell>;
  };

  const handleNumberChange = (
    filterName: string,
    type: 'min' | 'max',
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const value = event.target.value;
    const numericValue = value === '' ? undefined : parseFloat(value);
    onFilterValueChange(filterName, type, numericValue);
  };

  const getFilterValue = (filterName: string, type: 'min' | 'max'): string => {
    const filterValue = filterValues[filterName]?.[type];
    return filterValue !== undefined ? filterValue.toString() : '';
  };

  return (
    <Table size="small">
      <TableHead>
        <TableRow>
          <TableCell>
            <Typography variant="subtitle2" fontWeight="bold">
              Property Details
            </Typography>
          </TableCell>
          <TableCell align="center">
            <Typography variant="subtitle2" fontWeight="bold">
              Subject
            </Typography>
          </TableCell>
          <TableCell align="center">
            <Typography variant="subtitle2" fontWeight="bold">
              Comp Min.
            </Typography>
          </TableCell>
          <TableCell align="center">
            <Typography variant="subtitle2" fontWeight="bold">
              Comp Max.
            </Typography>
          </TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {EXPENSE_COMPS_DEFAULT_FILTERS.map((item, index) => {
          const isDate = item.name === 'expenseYear' || item.name === 'yearBuilt';
          const validator = dateValidators[item.name];

          return (
            <TableRow key={`${item.name}.${index}`}>
              <StyledTableCell>
                <Typography variant="body2">{item.title}</Typography>
              </StyledTableCell>
              <StyledTableCell align="center">{getStaticCell(item)}</StyledTableCell>
              <StyledTableCell align="center">
                <StyledNumberField
                  placeholder="min"
                  type="number"
                  size="small"
                  value={getFilterValue(item.name, 'min')}
                  onChange={(event) => handleNumberChange(item.name, 'min', event)}
                  inputProps={{
                    min: isDate ? 0 : undefined,
                    step: isDate ? 1 : 0.01,
                  }}
                  error={
                    !!validator &&
                    getFilterValue(item.name, 'min') !== '' &&
                    !!validator(parseFloat(getFilterValue(item.name, 'min')))
                  }
                  helperText={
                    validator &&
                    getFilterValue(item.name, 'min') !== '' &&
                    validator(parseFloat(getFilterValue(item.name, 'min')))
                  }
                />
              </StyledTableCell>
              <StyledTableCell align="center">
                <StyledNumberField
                  placeholder="max"
                  type="number"
                  size="small"
                  value={getFilterValue(item.name, 'max')}
                  onChange={(e) => handleNumberChange(item.name, 'max', e)}
                  inputProps={{
                    min: isDate ? 0 : undefined,
                    step: isDate ? 1 : 0.01,
                  }}
                  error={
                    !!validator &&
                    getFilterValue(item.name, 'max') !== '' &&
                    !!validator(parseFloat(getFilterValue(item.name, 'max')))
                  }
                  helperText={
                    validator &&
                    getFilterValue(item.name, 'max') !== '' &&
                    validator(parseFloat(getFilterValue(item.name, 'max')))
                  }
                />
              </StyledTableCell>
            </TableRow>
          );
        })}

        <TableRow>
          <StyledTableCell>
            <Typography variant="body2" fontWeight="bold">
              Expense Details
            </Typography>
          </StyledTableCell>
          <StyledTableCell align="center">
            <Typography variant="body2" fontStyle="italic" color="text.secondary">
              Subject Pro Forma
            </Typography>
          </StyledTableCell>
          <StyledTableCell />
          <StyledTableCell />
        </TableRow>

        {filters.map((item) => {
          const subjectExpense = get(subjectExpenses, item.name, {}) as SubjectExpenses[string];
          const subjectExpenseValue = expenseBasis === 'sf' ? subjectExpense?.sf : subjectExpense?.unit;

          return (
            <TableRow key={`${item.searchResultName}.index`}>
              <StyledTableCell>
                <Typography variant="body2">
                  {`${item.title}${expenseBasis === 'sf' ? PRICE_PER_SQUARE_FEET_POSTFIX : PRICE_PER_UNIT_POSTFIX}`}
                </Typography>
              </StyledTableCell>
              <StyledTableCell align="center">
                <StyledStaticCell>
                  {subjectExpense?.reported ? formatCurrencyFloat(subjectExpenseValue) : 'N/A'}
                </StyledStaticCell>
              </StyledTableCell>
              <StyledTableCell align="center">
                <StyledNumberField
                  placeholder="min"
                  type="number"
                  size="small"
                  value={getFilterValue(item.searchResultName, 'min')}
                  onChange={(e) => handleNumberChange(item.searchResultName, 'min', e)}
                  inputProps={{
                    step: 0.01,
                    min: 0,
                  }}
                />
              </StyledTableCell>
              <StyledTableCell align="center">
                <StyledNumberField
                  placeholder="max"
                  type="number"
                  size="small"
                  value={getFilterValue(item.searchResultName, 'max')}
                  onChange={(e) => handleNumberChange(item.searchResultName, 'max', e)}
                  inputProps={{
                    step: 0.01,
                    min: 0,
                  }}
                />
              </StyledTableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
};

export { ExpenseCompsFiltersTable };
